import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from '../../../components/Page';
import client from '../../../lib/feathers';

const EVENT_OPTIONS = [
  {
    value: 'app_updates',
    label: 'App updates & new features',
    description: 'Major releases, redesigns, and feature launches.',
  },
  {
    value: 'vinoseeker_news',
    label: 'News from VinoSeeker',
    description: 'Stories, partner announcements, and occasional letters from the team.',
  },
];

export default function NewBroadcast() {
  const navigate = useNavigate();
  const [eventKey, setEventKey] = useState('vinoseeker_news');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [recipientCount, setRecipientCount] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const refreshPreview = useCallback(async (key) => {
    setPreviewError(null);
    try {
      const res = await client.service('admin/user-broadcast-preview').find({
        query: { eventKey: key },
      });
      setRecipientCount(res.count);
    } catch (err) {
      setPreviewError(err.message || 'Could not load recipient count');
      setRecipientCount(null);
    }
  }, []);

  useEffect(() => {
    refreshPreview(eventKey);
  }, [eventKey, refreshPreview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !bodyHtml.trim()) {
      setSendError('Subject and body are required.');
      return;
    }
    const confirmed = window.confirm(
      `Send to ${recipientCount?.toLocaleString() || '?'} users? This cannot be undone.`,
    );
    if (!confirmed) return;
    setSending(true);
    setSendError(null);
    try {
      const result = await client.service('admin/user-broadcast').create({
        eventKey,
        subject: subject.trim(),
        bodyHtml,
      });
      navigate('/admin/broadcasts');
      console.log('broadcast sent:', result);
    } catch (err) {
      setSendError(err.message || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  return (
    <Page title="New User Broadcast">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold mb-2">Compose Broadcast</h1>
        <p className="text-sm text-gray-600 mb-6">
          Only users opted-in to the selected category will receive this email.
          An unsubscribe footer linking to <code className="px-1 bg-gray-100 rounded">/me/notifications</code>
          is appended automatically.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <div className="space-y-2">
              {EVENT_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-start gap-3 p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="eventKey"
                    value={opt.value}
                    checked={eventKey === opt.value}
                    onChange={(e) => setEventKey(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-sm">{opt.label}</div>
                    <div className="text-xs text-gray-600">{opt.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="px-3 py-2 rounded bg-blue-50 text-sm text-blue-900">
            {previewError && <span className="text-red-700">{previewError}</span>}
            {!previewError && recipientCount === null && 'Counting recipients…'}
            {!previewError && recipientCount !== null && (
              <span>
                <strong>{recipientCount.toLocaleString()}</strong> user{recipientCount === 1 ? '' : 's'} will receive this email.
              </span>
            )}
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
              maxLength={150}
              placeholder="Heads up — we now have notification preferences"
              required
            />
          </div>

          <div>
            <label htmlFor="bodyHtml" className="block text-sm font-medium text-gray-700 mb-1">Body (HTML)</label>
            <textarea
              id="bodyHtml"
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              rows={12}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:ring-red-500 focus:border-red-500"
              placeholder="<p>Hi there,</p><p>We just launched per-event notification preferences. If you'd rather not receive these announcements, you can opt out at the link below.</p>"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Plain HTML. Paragraphs (&lt;p&gt;), bold (&lt;strong&gt;), and links (&lt;a href&gt;) are most reliable.
            </p>
          </div>

          {sendError && (
            <div className="px-3 py-2 rounded bg-red-50 text-sm text-red-700">{sendError}</div>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/broadcasts')}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || recipientCount === 0 || recipientCount === null}
              className="px-4 py-2 text-sm text-white bg-red-700 rounded-md hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending…' : `Send to ${recipientCount?.toLocaleString() || '?'} users`}
            </button>
          </div>
        </form>
      </div>
    </Page>
  );
}
